import { Box, DialogContent, Popover, Typography } from '@mui/material'
import { endOfDay, startOfDay, subDays } from 'date-fns'
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CalendarSmallIcon from '@hapana/shared/web/styles/assets/svgs/calendar-small.svg'
import DatePicker from '@hapana/shared/web/components/shared/date-picker/date-picker.component'
import { VARIANTS } from '@hapana/shared/web/components/shared/date-picker/date-picker.constants'
import DialogLayout from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.component'
import {
  CLOSE_TYPES,
  SIZES,
} from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import Loading from '@hapana/shared/web/components/shared/loading/loading.component'
import { StatusPill } from '@hapana/shared/web/components/shared/status-pill/status-pill.component'
import { STATUS_I18N_KEY_MAP } from '_components/check-in-widget/most-recent-card/equipments-available/assign-to-equipment-dialog/assign-to-equipment-dialog.constants'
import { EQUIPMENT_ASSIGNMENT_STATUSES, EQUIPMENT_STATUSES } from '_constants/equipments'
import { useGetEquipment } from '_hooks/equipments.hooks'
import { useClientsByIds } from '_hooks/clients.hooks'
import { useGetSiteById } from '_hooks/sites.hooks'
import useSiteStore from '_stores/site.store'

import { styles } from './equipment-activity-log-dialog.styles'

const STATUS_DOT_COLOR = {
  [EQUIPMENT_STATUSES.AVAILABLE]: 'success.dark',
  [EQUIPMENT_STATUSES.IN_USE]: 'primary.main',
  [EQUIPMENT_STATUSES.DELAY]: 'primary.main',
  [EQUIPMENT_STATUSES.DIRTY]: 'warning.dark',
  [EQUIPMENT_STATUSES.DISABLED]: 'error.main',
  [EQUIPMENT_STATUSES.ERROR]: 'error.main',
}

const TimelineDot = ({ status }) => (
  <Box sx={[styles.timelineDot, { backgroundColor: STATUS_DOT_COLOR[status] ?? 'gray.40' }]} />
)

TimelineDot.propTypes = {
  status: PropTypes.string.isRequired,
}

const ActivityEntry = ({ entry, isLast }) => {
  const { t } = useTranslation(['checkIn'])
  const clientName = entry.client
    ? `${entry.client.firstName || ''} ${entry.client.lastName || ''}`.trim()
    : null

  return (
    <Box sx={styles.entryRow}>
      <Box sx={styles.timelineColumn}>
        <TimelineDot status={entry.displayStatus} />
        {!isLast && <Box sx={styles.timelineLine} />}
      </Box>
      <Box sx={[styles.entryContent, isLast && styles.entryContentLast]}>
        <StatusPill
          status={entry.displayStatus}
          label={t(
            `checkIn:equipments.assignToEquipment.${STATUS_I18N_KEY_MAP[entry.displayStatus]}`
          )}
        />
        <Box sx={styles.entryNameRow}>
          <Typography typography="header4" color="gray.64">
            {entry.equipmentName}
          </Typography>
          {clientName && (
            <>
              <Box sx={styles.bulletSeparator} />
              <Typography typography="body" color="gray.64">
                {t('checkIn:equipments.activityLog.by', { name: clientName })}
              </Typography>
            </>
          )}
        </Box>
        <Typography typography="body" color="gray.64" component="span">
          <Typography component="span" sx={styles.entryDateStr}>
            {entry.dateStr}
          </Typography>
          <Typography component="span" sx={styles.entryTimeStr}>
            {entry.timeStr}
          </Typography>
        </Typography>
      </Box>
    </Box>
  )
}

ActivityEntry.propTypes = {
  entry: PropTypes.shape({
    displayStatus: PropTypes.string.isRequired,
    equipmentName: PropTypes.string.isRequired,
    client: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
    dateStr: PropTypes.string.isRequired,
    timeStr: PropTypes.string.isRequired,
  }).isRequired,
  isLast: PropTypes.bool,
}

export const EquipmentActivityLogDialog = ({ equipmentId }) => {
  const { t } = useTranslation(['checkIn'])
  const { closeDialog } = useDialog()
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null)

  const { data: siteData } = useGetSiteById(siteId)
  const siteTimezone = siteData?.timezone

  const todayInSiteTimezone = useMemo(() => {
    const now = new Date()
    if (!siteTimezone) return now
    const year = parseInt(formatInTimeZone(now, siteTimezone, 'yyyy'), 10)
    const month = parseInt(formatInTimeZone(now, siteTimezone, 'MM'), 10) - 1
    const day = parseInt(formatInTimeZone(now, siteTimezone, 'dd'), 10)
    return new Date(year, month, day)
  }, [siteTimezone])

  const [dateRange, setDateRange] = useState(() => [
    startOfDay(subDays(new Date(), 30)),
    endOfDay(new Date()),
  ])

  // Once timezone loads, update the range to use the site's local date
  const initializedWithTimezone = useRef(false)
  useEffect(() => {
    if (!siteTimezone || initializedWithTimezone.current) return
    initializedWithTimezone.current = true
    setDateRange([startOfDay(subDays(todayInSiteTimezone, 30)), endOfDay(todayInSiteTimezone)])
  }, [siteTimezone, todayInSiteTimezone])

  const { data: equipmentData, isLoading } = useGetEquipment(siteId, equipmentId, {
    staleTime: 0,
    refetchOnMount: 'always',
  })
  const equipment = equipmentData?.equipment
  const equipmentName = equipment?.name ?? ''

  const allClientIds = useMemo(
    () => [...new Set((equipment?.assignments || []).map((a) => a.clientId).filter(Boolean))],
    [equipment?.assignments]
  )
  const { data: clientsMap } = useClientsByIds(allClientIds, siteId)

  const entries = useMemo(() => {
    const all = equipment?.assignments || []
    const tz = siteTimezone || 'UTC'
    const [startDate, endDate] = dateRange

    // Extract the browser-local calendar day (stripping any time component, whether midnight or
    // end-of-day) then convert start/end of that day to UTC in the site timezone.
    const calDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
    const startMs = startDate ? zonedTimeToUtc(startOfDay(calDay(startDate)), tz).getTime() : null
    const endMs = endDate ? zonedTimeToUtc(endOfDay(calDay(endDate)), tz).getTime() : null

    const isInRange = (ts) => {
      if (startMs == null || endMs == null) return true
      return ts >= startMs && ts <= endMs
    }

    const result = []

    for (const a of all) {
      const createdAt = a?.createdAt ? new Date(Number(a.createdAt)) : null
      if (!createdAt) continue

      const endTimestamp = a.doneAt
        ? new Date(Number(a.doneAt))
        : a.cancelledAt
          ? new Date(Number(a.cancelledAt))
          : null

      const endTimeStr = endTimestamp
        ? formatInTimeZone(endTimestamp, tz, 'h:mma')
        : t('checkIn:equipments.activityLog.inProgress')
      const client = a.clientId ? (clientsMap[a.clientId] ?? null) : null

      if (isInRange(createdAt.getTime())) {
        result.push({
          id: `${a.equipmentAssignmentId}-start`,
          displayStatus: EQUIPMENT_STATUSES.IN_USE,
          equipmentName,
          client,
          dateStr: formatInTimeZone(createdAt, tz, 'EEEE, MMM d, yyyy'),
          timeStr: ` - ${formatInTimeZone(createdAt, tz, 'h:mma')} – ${endTimeStr}`,
          sortAt: createdAt,
        })
      }

      if (endTimestamp && isInRange(endTimestamp.getTime())) {
        const endStatus =
          a.status === EQUIPMENT_ASSIGNMENT_STATUSES.DONE
            ? EQUIPMENT_STATUSES.DIRTY
            : EQUIPMENT_STATUSES.AVAILABLE
        result.push({
          id: `${a.equipmentAssignmentId}-end`,
          displayStatus: endStatus,
          equipmentName,
          client,
          dateStr: formatInTimeZone(endTimestamp, tz, 'EEEE, MMM d, yyyy'),
          timeStr: ` - ${formatInTimeZone(endTimestamp, tz, 'h:mma')}`,
          sortAt: endTimestamp,
        })
      }
    }

    // Add a synthetic entry for the current equipment status when it was set by a manual
    // operation (e.g. resetting from dirty → available) that has no assignment record.
    const STATUS_TO_LAST_AT = {
      [EQUIPMENT_STATUSES.AVAILABLE]: equipment?.lastAvailableAt,
      [EQUIPMENT_STATUSES.IN_USE]: equipment?.lastInUseAt,
      [EQUIPMENT_STATUSES.DELAY]: equipment?.lastDelayAt,
      [EQUIPMENT_STATUSES.DIRTY]: equipment?.lastDirtyAt,
    }
    const currentStatus = equipment?.status
    const rawCurrentAt = currentStatus ? STATUS_TO_LAST_AT[currentStatus] : null
    const currentStatusAt = rawCurrentAt ? new Date(Number(rawCurrentAt)) : null

    if (
      currentStatusAt &&
      !isNaN(currentStatusAt.getTime()) &&
      isInRange(currentStatusAt.getTime())
    ) {
      const alreadyCaptured = result.some((e) => e.sortAt.getTime() === currentStatusAt.getTime())
      if (!alreadyCaptured) {
        result.push({
          id: 'equipment-current-status',
          displayStatus: currentStatus,
          equipmentName,
          client: null,
          dateStr: formatInTimeZone(currentStatusAt, tz, 'EEEE, MMM d, yyyy'),
          timeStr: ` - ${formatInTimeZone(currentStatusAt, tz, 'h:mma')}`,
          sortAt: currentStatusAt,
        })
      }
    }

    return result.sort((a, b) => b.sortAt - a.sortAt)
  }, [equipment, dateRange, equipmentName, t, clientsMap, siteTimezone])

  return (
    <DialogLayout
      closeType={CLOSE_TYPES.ICON}
      closeAction={closeDialog}
      title={t('checkIn:equipments.activityLog.title')}
      size={SIZES.SM}
      primaryAction={{
        label: t('checkIn:equipments.activityLog.filterByDate'),
        action: (e) => setCalendarAnchorEl(e.currentTarget),
        props: { endIcon: CalendarSmallIcon },
      }}
    >
      <Popover
        open={Boolean(calendarAnchorEl)}
        anchorEl={calendarAnchorEl}
        onClose={() => setCalendarAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <DatePicker
          variant={VARIANTS.RANGE}
          value={dateRange}
          onChange={(newRange) => setDateRange(newRange)}
          maxDate={zonedTimeToUtc(
            `${formatInTimeZone(new Date(), siteTimezone || 'UTC', 'yyyy-MM-dd')}T23:59:59.999`,
            siteTimezone || 'UTC'
          )}
        />
      </Popover>

      <DialogContent sx={styles.dialogContent}>
        {isLoading ? (
          <Box sx={styles.loadingContainer}>
            <Loading />
          </Box>
        ) : entries.length === 0 ? (
          <Typography typography="medium" color="gray.64" sx={styles.emptyMessage}>
            {t('checkIn:equipments.activityLog.noActivity')}
          </Typography>
        ) : (
          <Box sx={styles.timeline}>
            {entries.map((entry, index) => (
              <ActivityEntry
                key={entry.id ?? index}
                entry={entry}
                isLast={index === entries.length - 1}
              />
            ))}
          </Box>
        )}
      </DialogContent>
    </DialogLayout>
  )
}

EquipmentActivityLogDialog.propTypes = {
  equipmentId: PropTypes.string.isRequired,
}

export default EquipmentActivityLogDialog
